/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useState } from "react";
import { useEnhancedSecurity } from "@/hooks/useEnhancedSecurity";
import { logger } from "@/lib/logger";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface SecurityContextType {
  securityAlerts: Array<{
    id: string;
    type: "high" | "medium" | "low";
    message: string;
    timestamp: number;
    requiresAction: boolean;
  }>;
  clearSecurityAlert: (alertId: string) => void;
  isSecurityCheckPassed: boolean;
  checkAccess: (operation: string, resourceUserId?: string) => Promise<boolean>;
  performSecureUpdate: (updates: Record<string, any>) => Promise<void>;
}

const SecurityContext = createContext<SecurityContextType | undefined>(
  undefined
);

export const SecurityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { profile } = useAuth();
  const {
    securityAlerts,
    clearSecurityAlert,
    checkServerAuthorization,
    secureProfileUpdate,
    canAccessResource,
  } = useEnhancedSecurity();

  const [isSecurityCheckPassed, setIsSecurityCheckPassed] = useState(false);

  // Initialize security check
  useEffect(() => {
    const performSecurityCheck = async () => {
      if (profile) {
        try {
          // Verify user session integrity
          const authorized = await checkServerAuthorization(
            "session_check",
            profile.id
          );
          setIsSecurityCheckPassed(authorized);

          if (!authorized) {
            toast.error("Security verification failed. Please log in again.");
          }
        } catch (error) {
          logger.error("Security check failed:", error);
          setIsSecurityCheckPassed(false);
        }
      }
    };

    performSecurityCheck();
  }, [profile, checkServerAuthorization]);

  // Show security alerts to therapists
  useEffect(() => {
    if (profile?.role === "therapist" && securityAlerts.length > 0) {
      const highPriorityAlerts = securityAlerts.filter(
        (alert) => alert.type === "high" && alert.requiresAction
      );

      highPriorityAlerts.forEach((alert) => {
        toast.error(`Security Alert: ${alert.message}`, {
          duration: 10000,
          action: {
            label: "Dismiss",
            onClick: () => clearSecurityAlert(alert.id),
          },
        });
      });
    }
  }, [securityAlerts, profile?.role, clearSecurityAlert]);

  const checkAccess = async (
    operation: string,
    resourceUserId?: string
  ): Promise<boolean> => {
    try {
      const hasAccess = await canAccessResource(resourceUserId, operation);
      if (!hasAccess) {
        toast.error(
          "Access denied. You do not have permission to perform this action."
        );
      }
      return hasAccess;
    } catch (error) {
      logger.error("Access check failed:", error);
      toast.error("Security check failed. Please try again.");
      return false;
    }
  };

  const performSecureUpdate = async (
    updates: Record<string, any>
  ): Promise<void> => {
    try {
      await secureProfileUpdate(updates);
      toast.success("Profile updated successfully");
    } catch (error) {
      logger.error("Secure update failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Update failed";
      toast.error(errorMessage);
      throw error;
    }
  };

  const contextValue: SecurityContextType = {
    securityAlerts,
    clearSecurityAlert,
    isSecurityCheckPassed,
    checkAccess,
    performSecureUpdate,
  };

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSecurity = (): SecurityContextType => {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error("useSecurity must be used within a SecurityProvider");
  }
  return context;
};
